import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  ChangeDetectorRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import {
  Router,
  Event,
  NavigationEnd,
  NavigationStart,
  NavigationCancel,
  ActivatedRoute,
} from '@angular/router';
import { AuthService } from './core/services/auth.service';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { LoadingIndicatorService } from './core/services/loading-indicator.service';
import { filter } from 'rxjs/operators';
import { ConnectionLostService } from './pages/secure/home/services/connection-lost.service';
import { UserInfo } from './shared/objects/auth/userinfo';
import { OverlayContainer } from '@angular/cdk/overlay';
import { CarrierService } from './pages/secure/home/services/carrier.service';
import { UserRole } from './shared/objects/auth/userRole';
import { Constants } from './shared/const/constants';
import {
  BrandingService,
  IBusinessUnitBranding,
} from './core/services/branding.service';
import { ModuleService } from './pages/secure/admin/shared/module.service';
import {
  Observable,
  Subscription,
  SubscriptionLike as ISubscription,
} from 'rxjs';
import { ModalService } from './core/services/modal.service';
import { CertificatesHubService } from './shared/services/certificates-hub.service';
import { CpModule } from './shared/objects/auth/cpModule';
import { Module } from './shared/objects/auth/module';
import { IncidentReportingService } from './pages/secure/order/shared/incident-reporting.service';
import { MatSidenav } from '@angular/material/sidenav';
import { GlobalResponsivenessService } from './core/services/global-responsiveness.service';
import { Breadcrumb } from './shared/objects/breadcrumb/breadcrumb.model';
import { Location } from '@angular/common';
import { BreadcrumbService } from './core/services/breadcrumb.service';
import { PageInfo } from './shared/objects/breadcrumb/page-info.model';
import { FiltersService } from './core/services/filters.service';
import { FreightExchangeService } from './pages/secure/freight-exchange/shared/freight-exchange.service';
import { IncidentService } from './pages/secure/incidents/incident.service';
import { AlwaysRefreshRoutesEnum } from './shared/navigation/always-refresh-routes.enum';
import { SystemSettingsService } from './pages/secure/home/services/system.settings.service';
import { PortalSetting } from './shared/objects/systemSettings/portal-setting';
import { PortalSettingEnum } from './shared/enums/portal-setting-type.enum';
import { ModuleFeatureEnum } from './shared/enums/module-feature.enum';
import { LayoutService } from './core/services/layout.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('snav', { static: true }) sideNav: MatSidenav;
  isNl = false;
  isEng = true;
  businessUnit: IBusinessUnitBranding;
  loadingMessage: string;
  currentRoute = '';
  lastRoute = '';
  noConnectionModalHidden = true;
  timeoutModalHidden = true;
  openIncidents: number = 0;
  nagivateInProgress = false;
  expiredCertificates: number = 0;
  public isMenuCollapsed = true;
  public userIsLogged = false;
  scrollable = false;

  fillerNav = Array.from({ length: 50 }, (_, i) => `Nav Item ${i + 1}`);

  carrierProfileModule: CpModule = {
    name: 'carrierProfile',
    translateKey: 'MAIN.CARRIER-PROFILE',
    path: '/secure/carriers',
    available: true,
    iconPath: null,
    isMainModule: true,
  };

  changePasswordModule: CpModule = {
    name: 'changePassword',
    translateKey: 'MAIN.CHANGE-PASSWORD',
    path: '/secure/change-password',
    available: true,
    iconPath: null,
    isMainModule: true,
  };

  homeModule: CpModule = {
    name: 'dashboard',
    translateKey: 'SHARED.HOME',
    path: '/secure/dashboard',
    available: true,
    iconPath: null,
    isMainModule: true,
  };

  helpModule: CpModule = {
    name: 'help',
    translateKey: 'MAIN.HELP',
    path: '/help',
    available: true,
    iconPath: null,
    isMainModule: true,
  };

  logoutModule: CpModule = {
    name: 'dashboard',
    translateKey: 'MAIN.LOGOUT',
    path: '/login',
    available: true,
    iconPath: null,
    isMainModule: true,
  };
  currentUserRole;

  public breadcrumbs: Breadcrumb[] = [];
  public isMobile = false;
  public isTablet = false;

  private subscriptionTimeoutStatus: ISubscription;
  private subscriptionLostConnection: ISubscription;
  public Module = Module;
  currentNavigatedUrl: string;
  FilterService = FiltersService;
  get isImpersonated() {
    return this.authService.isImpersonated();
  }
  get outsideAppContext(): boolean {
    return (
      this.currentRoute.indexOf('/login') === -1 ||
      this.currentRoute.indexOf('/register') === -1
    );
  }
  get isDetailsRoute(): boolean {
    const detailsRoutes = [
      '/order-list/',
      '/transport',
      '/ex-work-timeslot/',
      '/incidents/',
      '/carriers/',
      '/freight-exchange/',
      '/users/',
      '/approve-carriers/',
      '/tender/',
    ];
    return detailsRoutes.some(route => this.currentRoute.includes(route));
  }

  get insideAppContext(): boolean {
    const excludedRoutes = [
      '/login',
      '/register',
      '/account-disabled',
      '/reset-password',
      '/help',
    ];
    return !excludedRoutes.some(route => this.currentRoute.includes(route));
  }

  get canOutsideAppContextRoutesFromHelp(): boolean {
    return this.getUserInfo !== undefined && this.getUserInfo !== null;
  }

  get dashboardAppContext(): boolean {
    return this.currentRoute === '/secure/dashboard';
  }

  get isFullScreenMessage(): boolean {
    return !this.noConnectionModalHidden || !this.timeoutModalHidden;
  }

  get isLogoScreen(): boolean {
    return this.currentRoute.indexOf('account-disabled') !== -1;
  }

  get showUploadCertificatesBadge(): boolean {
    return this.expiredCertificates > 0;
  }

  get currentVersion(): string {
    if (environment.production) {
      return `${environment.version.version}`;
    } else {
      return `${environment.version.version} - ${environment.version.hash}`;
    }
  }

  get getUserInfo(): UserInfo {
    return this.authService.getStoredUserInfo();
  }

  get logoPath(): string {
    return this.brandingService.getLogoPath();
  }

  get footerDetails(): string {
    return this.brandingService.getFooterDetails();
  }

  get AvailableModules() {
    const modules = this.authService.getStoredUserInfo()
      ? this.moduleService.getAvailableModules()
      : [];
    return modules;
  }

  get availableModulesForMenu() {
    if (this.showInstructionDocModule) {
      return this.AvailableModules;
    }

    return this.AvailableModules.filter(
      module => module.name !== Module.InstructionDocs.toString(),
    );
  }

  get showInstructionDocModule(): boolean {
    if (!this.shouldApplyInstructionDocVisibility) {
      return true;
    }

    const instructionDocSetting = this.portalSettings.find(
      portalSetting =>
        portalSetting.type === PortalSettingEnum.Boolean &&
        (portalSetting.moduleFeature & ModuleFeatureEnum.ShowInstructionDoc) ===
          ModuleFeatureEnum.ShowInstructionDoc,
    );

    return !!instructionDocSetting;
  }

  get shouldApplyInstructionDocVisibility(): boolean {
    return (
      this.currentUserRole === UserRole.CarrierAdmin ||
      this.currentUserRole === UserRole.CarrierUser
    );
  }

  get canSeeMyProfile() {
    return (
      this.AvailableModules.length > 0 &&
      this.currentUserRole === UserRole.CarrierAdmin
    );
  }

  get canSeeHelp() {
    return (
      this.AvailableModules.length > 0 &&
      this.currentUserRole !== UserRole.PortalAdmin &&
      this.currentUserRole !== UserRole.BusinessAdmin
    );
  }

  get currentModule(): CpModule {
    const anyCoreModule = this.AvailableModules.find(availableModule =>
      availableModule.path.includes(this.currentRoute),
    );

    const module = anyCoreModule
      ? anyCoreModule
      : this.homeModule.path.includes(this.currentRoute)
        ? this.homeModule
        : null;
    return module;
  }

  get showBreadcrumb() {
    return this.breadcrumbs.some(breadcrumb => breadcrumb.showOwnPage);
  }

  private allBreadcrumbs: Breadcrumb[] = [];
  private subscriptions: Subscription[] = [];
  private fragments;
  currentBreadcrumb: Breadcrumb;
  UserRole = UserRole;
  freightExchangeOrders = 0;
  FreightExchangeService: FreightExchangeService;
  IncidentService: IncidentService;
  AlwaysRefreshRoutesEnum = AlwaysRefreshRoutesEnum;
  private portalSettings: PortalSetting[] = [];
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    public sanitizer: DomSanitizer,
    public translate: TranslateService,
    private loadingIndicatorService: LoadingIndicatorService,
    private connectivityService: ConnectionLostService,
    private overlayContainer: OverlayContainer,
    private modalService: ModalService,
    private carrierService: CarrierService,
    private brandingService: BrandingService,
    private moduleService: ModuleService,
    private changeDetectorRef: ChangeDetectorRef,
    private certificatesHub: CertificatesHubService,
    private incidentService: IncidentReportingService,
    private freightExchangeService: FreightExchangeService,
    private globalResponsive: GlobalResponsivenessService,
    private systemSettingsService: SystemSettingsService,

    private breadcrumbService: BreadcrumbService,

    private layoutService: LayoutService,
  ) {
    this.allBreadcrumbs = this.breadcrumbService.breadcrumbs;
    this.authService.init();
    translate.setDefaultLang('en');

    if (!!localStorage.getItem('cp-language')) {
      this.setLanguage(localStorage.getItem('cp-language'));
    } else {
      this.setLanguage('en');
    }

    this.subscriptionLostConnection =
      this.connectivityService.connectionStatus.subscribe(isOnline => {
        this.noConnectionModalHidden = isOnline;
        this.changeDetectorRef.markForCheck();
      });
    this.subscriptionTimeoutStatus = this.authService.timeoutStatus.subscribe(
      isTimeout => {
        if (isTimeout) {
          this.modalService.closeAll();
        }
        this.timeoutModalHidden = !isTimeout;
        this.changeDetectorRef.markForCheck();
      },
    );

    router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe((e: NavigationStart) => {
        this.determineLoadingMessage(e.url);
        this.nagivateInProgress = true;
        this.loadingIndicatorService.showFetchData();
      });
    router.events
      .pipe(
        filter(
          e => e instanceof NavigationEnd || e instanceof NavigationCancel,
        ),
      )
      .subscribe(() => {
        this.nagivateInProgress = false;
        this.loadingIndicatorService.hideFetchData();
        this.changeDetectorRef.markForCheck();
      });

    this.certificatesHub.connectToHubWithAutoConnect();

    const attributes = this.globalResponsive.getResolutionAttributes();
    this.isTablet = attributes.isTablet;
    this.isMobile = attributes.isMobile;
  }

  private checkForDetailBreadcrumb(
    moduleBreadcrumbs: Breadcrumb[],
    pageInfo: PageInfo,
  ): boolean {
    if (moduleBreadcrumbs.length === 0) {
      return false;
    }
    const moduleFragmentIndex = moduleBreadcrumbs.findIndex(
      breadcrumb => breadcrumb.route === this.fragments[0],
    );
    if (!pageInfo.id) {
      //* Don't do anything because details don't matter now
      return false;
    }
    if (moduleFragmentIndex === -1) {
      const detailBreadcrumb = moduleBreadcrumbs[0];
      detailBreadcrumb.subTitle = !!detailBreadcrumb.subTitle
        ? detailBreadcrumb.subTitle
        : !!pageInfo.viewId
          ? pageInfo.viewId
          : this.fragments[0];
      detailBreadcrumb.absoluteRoute += `/${this.fragments[0]}`;
      this.fragments.shift();
      moduleBreadcrumbs.shift();

      //* Details module push
      this.breadcrumbs.push(detailBreadcrumb);
      this.currentBreadcrumb = detailBreadcrumb;
      return true;
    }
    return false;
  }

  ngOnInit(): void {
    this.breadcrumbService.pageInfoSubject$.subscribe(pageInfo => {
      this.breadcrumbs = [];
      this.fragments = this.currentNavigatedUrl.split('/');
      //* Consume the empty path
      this.fragments.shift();

      const moduleFragmentIndex = this.allBreadcrumbs.findIndex(
        breadcrumb => breadcrumb.route === this.fragments[0],
      );
      if (moduleFragmentIndex !== -1) {
        this.currentBreadcrumb = JSON.parse(
          JSON.stringify(this.allBreadcrumbs[moduleFragmentIndex]),
        );
        this.breadcrumbs.push(this.currentBreadcrumb);
        this.fragments.shift();

        while (this.fragments.length > 0) {
          const isDetails = this.checkForDetailBreadcrumb(
            this.currentBreadcrumb.breadcrumbs,
            pageInfo,
          );

          if (!isDetails) {
            const moduleBreadcrumb = this.currentBreadcrumb.breadcrumbs.find(
              breadcrumb => breadcrumb.route === this.fragments[0],
            );
            if (moduleBreadcrumb === undefined) {
              this.fragments.shift();
              continue;
            }

            const currentKey = Object.keys(pageInfo).find(
              key => pageInfo[key] === this.fragments[0],
            );

            const currentValue =
              currentKey !== undefined ? pageInfo[currentKey] : undefined;
            moduleBreadcrumb.absoluteRoute.replace(
              this.breadcrumbService.idWildcard,
              currentValue,
            );
            this.breadcrumbs.push(moduleBreadcrumb);
            this.currentBreadcrumb = { ...moduleBreadcrumb };
            //* Consume the  fragment
            this.fragments.shift();
          }
        }
      }
      this.changeDetectorRef.detectChanges();
    });

    this.layoutService.scrollable$.subscribe(v => {
      this.scrollable = v;
    });

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.currentNavigatedUrl = event.url;
        if (event.url.indexOf('login') === -1) {
          this.lastRoute = this.currentRoute;
        }
        this.currentRoute = event.urlAfterRedirects;
        if (!this.isDetailsRoute) {
          this.breadcrumbService.updatePageInfo({ id: null });
        }
      }
    });
    this.setCurrentBusinessUnit();
    this.setAppIcon();

    this.globalResponsive.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
      this.changeDetectorRef.markForCheck();
    });
    this.globalResponsive.isTablet$.subscribe(isTablet => {
      this.isTablet = isTablet;
      this.changeDetectorRef.markForCheck();
    });
    this.authService.userIsLogged.subscribe(logged => {
      this.userIsLogged = logged;

      if (this.authService.userHasValidAccessToken()) {
        this.authService.setupUserInactivityTimeoutContext();
        this.currentUserRole = this.authService.getUserRole();
        this.checkRelatedSidemenuData();
        this.loadPortalSettings();
      }
    });
    this.certificatesHub.expiredCertificatesEmitter.subscribe(
      (expired: number) => {
        if (expired != null) {
          this.expiredCertificates = expired;
          this.changeDetectorRef.detectChanges();
        }
      },
    );
    this.subscriptions.push(
      this.sideNav.openedStart.subscribe(() => {
        this.checkRelatedSidemenuData();
      }),
    );
  }

  private checkRelatedSidemenuData() {
    const userPermissions = this.authService.getUserFeatureClaims();

    const userHasAccessIncidents = userPermissions.includes(Module.Incidents);
    const userHasAccessFreightExchange = userPermissions.includes(
      Module.FreightExchange,
    );

    const shouldCheckCertificatesState =
      this.AvailableModules.length > 0 &&
      this.currentUserRole !== UserRole.PortalAdmin &&
      this.currentUserRole !== UserRole.BusinessAdmin;

    if (shouldCheckCertificatesState) {
      this.getExpiredCertificates();
    }
    if (userHasAccessIncidents) {
      this.incidentService.getIncidentsNumber().subscribe(incidents => {
        this.openIncidents = incidents;
        this.changeDetectorRef.markForCheck();
      });
    }
    if (userHasAccessFreightExchange) {
      this.freightExchangeService
        .getAllFreightExchangesNumber()
        .subscribe(number => {
          this.freightExchangeOrders = number;
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  public navigateBack() {
    this.breadcrumbService.updatePage(null);
  }
  public routeTo(route: string): void {
    if (!this.isDetailsRoute) {
      this.router.navigate([route]);
      if (route === AlwaysRefreshRoutesEnum.FreightExchange) {
        FreightExchangeService.updateFreightList();
      }
      if (route === AlwaysRefreshRoutesEnum.Incidents) {
        IncidentService.updateIncidentList();
      }
    }
    this.breadcrumbService.updatePage({ route });
  }

  showIncidentsBadge(module: CpModule): boolean {
    return (
      this.openIncidents != 0 &&
      module.name == Module.Incidents.toString() &&
      module.available
    );
  }
  showFreightExchangeBadge(module: CpModule): boolean {
    return (
      this.freightExchangeOrders != 0 &&
      module.name == Module.FreightExchange.toString() &&
      module.available
    );
  }

  getExpiredCertificates() {
    this.carrierService.getExpiredCertificatesNumber().subscribe(expired => {
      this.expiredCertificates = expired;
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.subscriptionTimeoutStatus.unsubscribe();
    this.subscriptionLostConnection.unsubscribe();
    this.certificatesHub.disconnectFromHub();
  }

  setLanguage(language: string) {
    this.isEng = language === 'en';
    this.isNl = !this.isEng;

    this.translate.use(language);

    localStorage.setItem('cp-language', language);
  }

  help() {
    this.router.navigate(['/help']);
  }

  logout() {
    FiltersService.initFilters();

    if (!this.isDetailsRoute) {
      this.authService.logout();
      this.router.navigate(['/login']);
    }
    this.breadcrumbService.updatePage({ route: '/login', isLogout: true });
  }

  backHome() {
    const user = this.getUserInfo;
    if (user) {
      this.router.navigate(['/secure/dashboard']);
    } else {
      this.router.navigate(['/login']);
    }
  }

  reload() {
    this.connectivityService.checkConnectivityStatus();
    if (!this.timeoutModalHidden) {
      this.logout();
      this.timeoutModalHidden = true;
    } else {
      this.router.navigate([this.lastRoute]);
    }
  }

  myProfile() {
    const currentCarrierUsername = this.authService.getStoredUserInfo();
    if (
      !currentCarrierUsername ||
      currentCarrierUsername.preferred_username === ''
    ) {
      return;
    }

    this.carrierService
      .getCarrierByUsername(currentCarrierUsername.preferred_username)
      .then(res => {
        this.router.navigate(['secure', 'carriers', 'my-profile']);
      });
  }

  changePassword() {
    this.router.navigate(['/secure/change-password']);
  }

  private setCurrentBusinessUnit() {
    environment.businessunits.forEach(el => {
      if (window.location.host === el.url) {
        this.businessUnit = el;
        return;
      }
    });
    // remove old theme class and add new theme class
    // we're removing any css class that contains '-theme' string but your theme classes can follow any pattern
    const overlayContainerClasses =
      this.overlayContainer.getContainerElement().classList;
    const themeClassesToRemove = Array.from(overlayContainerClasses).filter(
      (item: string) => item.includes('-theme'),
    );
    if (themeClassesToRemove.length) {
      overlayContainerClasses.remove(...themeClassesToRemove);
    }
    overlayContainerClasses.add(this.businessUnit.theme);
  }

  private setAppIcon() {
    document
      .getElementById('appIcon')
      .setAttribute('href', this.businessUnit.icon);
  }

  private determineLoadingMessage(route: string): void {
    switch (route) {
      case Constants.ORDERS_OVERVIEW_URL:
        this.getLoadingMessage('MAIN.LOADING-ORDERS');
        break;
      case Constants.FREIGHT_EXCHANGE_URL:
        this.getLoadingMessage('MAIN.LOADING-ORDERS');
        break;
      case Constants.SELF_BILLING_URL:
        this.getLoadingMessage('MAIN.LOADING-SELFBILLINGS');
        break;
      case Constants.USERS_URL:
        this.getLoadingMessage('MAIN.LOADING-USERS');
        break;
      case Constants.CARRIERS_URL:
        this.getLoadingMessage('MAIN.LOADING-CARRIERS');
        break;
      case Constants.CARRIERS_TO_APPROVE_URL:
        this.getLoadingMessage('MAIN.LOADING-CARRIERS-TO-APPROVE');
        break;
      case Constants.PORTAL_SETTINGS_URL:
        this.getLoadingMessage('MAIN.PORTAL-SETTINGS');
        break;
      case Constants.EXWORK_ORDERS_OVERVIEW_URL:
        this.getLoadingMessage('MAIN.LOADING-EXWORK-ORDERS');
        break;
      case Constants.TENDERS_URL:
        this.getLoadingMessage('MAIN.LOADING-TENDERS');
        break;
      case Constants.INSTRUCTION_DOCS_URL:
        this.getLoadingMessage('MAIN.LOADING-INSTRUCTION-DOCS');
        break;
      case Constants.INCIDENTS_URL:
        this.getLoadingMessage('MAIN.LOADING-INCIDENTS');
        break;
      default:
        this.getLoadingMessage('MAIN.LOADING');
        break;
    }
  }

  private getLoadingMessage(key: string): void {
    this.translate
      .get(key)
      .toPromise()
      .then(message => {
        this.loadingMessage = message;
      });
  }

  private loadPortalSettings(): void {
    this.systemSettingsService.getPortalSettings().subscribe(portalSettings => {
      this.portalSettings = portalSettings;
      this.changeDetectorRef.markForCheck();
    });
  }
}
